import { useEffect, useState } from "react";
import { api, thumbUrl, fileUrl } from "./api";

let cache = null;
let promise = null;
const listeners = new Set();

// Call after Terms Management / Appearance mutations so cards refresh fallbacks.
export function clearTermImageCache() {
  cache = null;
  promise = null;
  listeners.forEach((fn) => fn());
}

function fetchMap() {
  if (cache) return Promise.resolve(cache);
  if (!promise) {
    promise = api.get("/term-images")
      .then((r) => { cache = r.data; return cache; })
      .catch(() => ({ images: {}, settings: {} }));
  }
  return promise;
}

export function useTermImages() {
  const [data, setData] = useState(cache || { images: {}, settings: {} });
  useEffect(() => {
    const fn = () => fetchMap().then(setData);
    listeners.add(fn);
    fetchMap().then(setData);
    return () => listeners.delete(fn);
  }, []);
  return data;
}

const TERM_FIELDS = {
  event: (e) => [["event_type", e.event_type], ["genre", e.genre]],
  venue: (v) => [["venue_type", v.venue_type]],
  artist: (a) => [...(a.genres || (a.genre ? [a.genre] : [])).map((g) => ["genre", g]), ["artist_type", a.artist_type]],
  gig: (g) => [["genre_needed", g.genre_needed], ["performance_type_needed", g.performance_type_needed]],
  deal: (d) => [["deal_category", d.category]],
  hiring: (h) => [["job_category", h.job_category]],
  attraction: (x) => [["attraction_type", x.attraction_type]],
  activity: (x) => [["activity_type", x.activity_type]],
  business: (x) => [["business_type", x.business_type]],
};

const OWN_IMAGE = {
  event: (e) => e.event_image,
  venue: (v) => v.hero_image || v.logo,
  artist: (a) => a.hero_image || a.profile_image,
  gig: (g) => g.image_url || g.image,
  deal: (d) => d.image || d.image_url,
  hiring: (h) => h.image || h.image_url,
  attraction: (x) => x.hero_image || x.logo,
  activity: (x) => x.hero_image || x.logo,
  business: (x) => x.hero_image || x.logo,
};

const galleryFirst = (ent) => {
  const g = ent.gallery || ent.gallery_images || [];
  const first = Array.isArray(g) ? g[0] : null;
  if (!first) return "";
  return typeof first === "string" ? first : first.image_url || first.url || first.image || "";
};

export function resolveListingImage(ent, kind, data) {
  if (!ent) return { src: "", alt: "" };
  const { images = {}, settings = {} } = data || {};
  const name = ent.title || ent.name || "";
  const own = OWN_IMAGE[kind]?.(ent) || "";
  if (own) return { src: own, alt: name };
  const gal = galleryFirst(ent);
  if (gal) return { src: gal, alt: name };
  if (settings.term_image_fallback !== false) {
    for (const [tax, val] of TERM_FIELDS[kind]?.(ent) || []) {
      if (!val) continue;
      const hit = images[`${tax}:${String(val).toLowerCase().trim()}`];
      if (hit) return { src: hit.image, alt: hit.alt || String(val) };
    }
  }
  const city = ent.city || ent.home_city;
  if (settings.city_image_fallback !== false && city) {
    const hit = images[`city:${String(city).toLowerCase().trim()}`];
    if (hit) return { src: hit.image, alt: hit.alt || city };
  }
  const state = ent.state || ent.home_state;
  if (settings.state_image_fallback !== false && state) {
    const hit = images[`state:${String(state).toLowerCase().trim()}`];
    if (hit) return { src: hit.image, alt: hit.alt || state };
  }
  const dflt = settings[`default_${kind}_image`];
  if (dflt) return { src: dflt, alt: name };
  return { src: "", alt: name };
}

// Hook: resolved image for a listing card (thumb) or hero (full).
export function useListingImage(ent, kind, opts = {}) {
  const data = useTermImages();
  const { src, alt } = resolveListingImage(ent, kind, data);
  if (!src) return { src: "", alt };
  return { src: opts.thumb === false ? fileUrl(src) : thumbUrl(src), alt };
}

const HERO_FALLBACK = "https://images.pexels.com/photos/3122799/pexels-photo-3122799.jpeg?w=1200";

export function ListingHeroImg({ ent, kind, className }) {
  const { src, alt } = useListingImage(ent, kind, { thumb: false });
  return <img src={src || HERO_FALLBACK} alt={alt} className={className} />;
}
